import { useEffect, useMemo, useRef, useState } from "react"
import { Line } from "react-chartjs-2"
import { Chart, registerables, TooltipItem, TooltipModel } from "chart.js"

import { useLazyGetNewClientsTrendWidgetQuery } from "store/api/clientApi"
import { UITypes } from "types"

import { SelectStatic } from "components/form/SelectStatic"
import { dateSelectOptions } from "pages/vendor/Dashboard/constants"

import {
  ActiveCustomersLabel,
  ActiveCustomersValue,
  ChartContainer,
  ContentWrapper,
  DataColumn,
  DaysText,
  PercentageRow,
  PercentageValue,
  SelectWrapper,
  StatsRow,
  StyledArrowIcon,
  Title,
  WidgetHeader,
  WidgetWrapper,
} from "./styled"

Chart.register(...registerables)

export const WidgetNewCustomersTrend = () => {
  const [selectedDate, setSelectedDate] = useState(dateSelectOptions[0])
  const [triggerGetNewClientsTrendWidget, { data }] = useLazyGetNewClientsTrendWidgetQuery()
  const chartRef = useRef<Chart<"line"> | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        await triggerGetNewClientsTrendWidget({
          period: Number(selectedDate.value),
        })
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [selectedDate, triggerGetNewClientsTrendWidget])

  const handleSelectDate = (date: UITypes.Option[]) => {
    setSelectedDate(date[0])
  }

  const percentageDifference = useMemo(() => {
    if (!data?.data) return null

    const { last_period, prev_period } = data.data

    if (prev_period === 0) {
      return last_period > 0 ? 100 : 0
    }

    return ((last_period - prev_period) / prev_period) * 100
  }, [data])

  const isPositive = percentageDifference !== null && percentageDifference >= 0
  const isZero = percentageDifference !== null && Math.abs(percentageDifference) < 0.01

  const getChartColorRGB = () => {
    if (isZero) {
      return { r: 148, g: 163, b: 184 }
    } else if (!isPositive) {
      return { r: 239, g: 68, b: 68 }
    }
    return { r: 34, g: 197, b: 94 }
  }

  const chartData = useMemo(() => {
    if (!data?.data?.date_data || data.data.date_data.length === 0) {
      return {
        labels: [],
        datasets: [
          {
            label: "",
            data: [],
            borderColor: "#22C55E",
            backgroundColor: "transparent",
            borderWidth: 2,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
          },
        ],
      }
    }

    return {
      labels: data.data.date_data.map(item => item.date),
      datasets: [
        {
          label: "",
          data: data.data.date_data.map(item => item.count),
          borderColor: (context: { chart: Chart }) => {
            const ctx = context.chart.ctx
            const chart = context.chart
            const gradient = ctx.createLinearGradient(0, 0, chart.width, 0)
            const { r, g, b } = getChartColorRGB()

            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0)`)
            gradient.addColorStop(0.4, `rgba(${r}, ${g}, ${b}, 1)`)
            return gradient
          },
          backgroundColor: (context: { chart: Chart }) => {
            const ctx = context.chart.ctx
            const gradient = ctx.createLinearGradient(0, 0, 0, 80)
            const { r, g, b } = getChartColorRGB()

            gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.3)`)
            gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)
            return gradient
          },
          borderWidth: 2,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: "#ffffff",
          fill: true,
        },
      ],
    }
  }, [data, isPositive, isZero])

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: "index" as const,
        intersect: false,
        callbacks: {
          title: (context: Array<{ label?: string }>) => {
            return context[0]?.label || ""
          },
          label: function (this: TooltipModel<"line">, tooltipItem: TooltipItem<"line">) {
            if (tooltipItem.parsed.y === null) {
              return "Count: N/A"
            }
            return `Count: ${tooltipItem.parsed.y}`
          },
        },
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#e2e8f0",
        borderWidth: 1,
        padding: 8,
        displayColors: false,
        titleFont: {
          size: 12,
          weight: "normal" as const,
        },
        bodyFont: {
          size: 12,
        },
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
      },
    },
  }

  const activeCustomers = data?.data?.last_period ?? 0

  return (
    <WidgetWrapper>
      <WidgetHeader>
        <Title>New Customers</Title>
        <SelectWrapper>
          <SelectStatic values={[selectedDate]} onChange={handleSelectDate} options={dateSelectOptions} />
        </SelectWrapper>
      </WidgetHeader>
      <ContentWrapper>
        <DataColumn>
          <StatsRow>
            <ActiveCustomersValue>{activeCustomers}</ActiveCustomersValue>
            <ActiveCustomersLabel>Active customers</ActiveCustomersLabel>
          </StatsRow>
          {percentageDifference !== null && (
            <>
              <PercentageRow>
                {!isZero && <StyledArrowIcon isPositive={isPositive} />}
                <PercentageValue isPositive={isPositive} isZero={isZero}>
                  {Math.abs(percentageDifference).toFixed(1)}%
                </PercentageValue>
              </PercentageRow>
              <DaysText>past {selectedDate.value} days</DaysText>
            </>
          )}
        </DataColumn>
        <ChartContainer>
          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </ChartContainer>
      </ContentWrapper>
    </WidgetWrapper>
  )
}
